<template lang="pug">
.other-office-room-schedule-table-component
  .b-table-sticky-header
    b-table-simple.table.b-table.basic-table.b-table-no-border-collapse
      b-thead
        b-tr
          b-th.select
            b-form-checkbox.ml-2(v-model="_allSelect" :name="'checkbox-all'")
          b-th.facility 施設
          b-th.use-case 用途
          b-th.room-type 部屋種別
          b-th.day(v-for="day in fields" :key="day" :id="day")
            .d-flex.flex-column
              span {{ getDay(day) }}
              .week
                span （
                span(:class="getWeekClass(day)") {{ getWeek(day) }}
                span ）
            b-popover(v-if="getHolidayName(day)" :target="day" triggers="hover")
              span {{ getHolidayName(day) }}
      b-tbody(v-if="isLoading == true")
        b-tr
          b-td(:colspan="3 + fields.length")
            .text-center.table-loading-message
              b-spinner.spinner
              span.message.align-self-center 読込み中...

      b-tbody(v-else v-for="facility in _value")
        template(v-if="facility.publicType == 'schedule'" v-for="(useCase, useCaseIndex) in facility.useCasesForRoomSchedule")
          b-tr(v-for="(room, roomIndex) in useCase.rooms")
            b-td.select(v-if="useCaseIndex == 0 && roomIndex == 0" :rowspan="getFacilityRowSapan(facility)")
              b-form-checkbox.ml-2(
                v-if="showCheckbox(facility)"
                v-model="facility.checked" :name="'checkbox-' + facility.facilityId"
                @change="changeFacilityCheck")
            b-td.facility(v-if="useCaseIndex == 0 && roomIndex == 0" :rowspan="getFacilityRowSapan(facility)"
              :id="`${facility.facilityId}`") {{ convertFacilityName(facility) }}
            b-popover(v-if="facility.message" :target="`${facility.facilityId}`" triggers="hover")
              pre.message {{ facility.message }}
            b-td.use-case(v-if="roomIndex == 0" :rowspan="useCase.rooms.length") {{ convertUseTypeName(useCase.useType) }}
            b-td.room-type {{ convertRoomTypeNameForFamilyService(room.roomType) }}
            b-td.day.text-center(v-for="day in room.days") {{ convertReceptionStatus(day) }}

        template(v-if="facility.publicType == 'custom'" v-for="(useCase, useCaseIndex) in facility.useCasesForVacancySetting")
          b-tr(v-for="(room, roomIndex) in useCase.rooms")
            b-td.select(v-if="useCaseIndex == 0 && roomIndex == 0" :rowspan="getFacilityRowSapan(facility)")
              b-form-checkbox.ml-2(
                v-if="showCheckbox(facility)"
                v-model="facility.checked" :name="'checkbox-' + facility.facilityId"
                @change="changeFacilityCheck"
              )
            b-td.facility(v-if="useCaseIndex == 0 && roomIndex == 0" :rowspan="getFacilityRowSapan(facility)"
              :id="`${facility.facilityId}`") {{ convertFacilityName(facility) }}
            b-popover(v-if="facility.message" :target="`${facility.facilityId}`" triggers="hover")
              pre.message {{ facility.message }}
            b-td.use-case(v-if="roomIndex == 0" :rowspan="useCase.rooms.length") {{ convertUseCaseName(useCase.useCase) }}
            b-td.room-type {{ convertRoomTypeName(room.roomType) }}
            b-td.day.custom(:colspan="3")
              span(style="margin-left: 13px;") {{ convertReceptionStatusAndCount(room) }}
            b-td.day.custom-spacer(:colspan="fields.length - 3")
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";

import { userStore } from "@/store/store";
import { nationalHolidaysStore } from "@/store/store";

import { NationalHolidaysGetModel } from "@/commons/services/NationalHolidays";
import { PublicType, ReceptionCount, ReceptionStatus, RoomType, UseCase } from "@/commons/services/RoomScheduleSettings";
import { OtherOfficeRoomSchedulesGetModel, RoomForVacancySetting } from "@/commons/services/OtherOfficeRoomSchedules";
import { RoomType as RoomScheduleRoomType, UseType } from "@/commons/services/Rooms";
import { CareLevels, FacilityCategory, MedicalNursings } from "@/commons/services/Facilities";

/**
 * ### 仮押さえ／転床テーブルコンポーネント
 */
@Component({})
export default class RoomScheduleTable extends Vue {
  /** 1行あたりの情報配列 */
  @Prop()
  value!: Array<RoomScheduleTableModel>;
  /** 列名配列 */
  @Prop()
  fields!: Array<string>;
  /** ローディング表示可否 */
  @Prop()
  isLoading!: boolean;
  /** フィルター */
  @Prop()
  filter!: FilterSetting;
  /** 全て選択列のチェック */
  @Prop()
  allSelect!: boolean;

  /** 祝日MAP */
  holidaysMap: Map<string, NationalHolidaysGetModel> = new Map();

  weekNameMap: Map<number, string> = new Map([
    [0, "日"],
    [1, "月"],
    [2, "火"],
    [3, "水"],
    [4, "木"],
    [5, "金"],
    [6, "土"]
  ]);

  /** ### フィルタリングした情報を返す */
  get _value(): Array<RoomScheduleTableModel> {
    // フィルタリング
    return this.value.filter((facility) => {
      return (
        this.filteringRegion(facility) &&
        this.filteringFacilityCategory(facility) &&
        this.filteringCareLevels(facility) &&
        this.filterOnlyAcceptsWelfare(facility) &&
        this.filterMedicalNursings(facility) &&
        this.filterOnlyBlankRoom(facility)
      );
    });
  }

  get _allSelect(): boolean {
    return this.allSelect;
  }

  set _allSelect(newValue: boolean) {
    this.$emit("update:allSelect", newValue);
    this.$emit("onAllSelect");
    return;
  }

  showCheckbox(facility: RoomScheduleTableModel): boolean {
    return facility.id == userStore.facility.id ? false : true;
  }

  /**
   * ### 地域情報フィルタリング
   */
  private filteringRegion(facility: RoomScheduleTableModel): boolean {
    // フィルタリングの地域が空文字（全国）なら全てを返す
    if (!this.filter.prefecture) {
      return true;
    }

    // 地域情報がない場合は非表示で扱う
    if (!facility.facilityDetailInfo || !facility.facilityDetailInfo.region) {
      return false;
    }

    // 都道府県でフィルタリング
    if (facility.facilityDetailInfo.region.prefectureCode !== this.filter.prefecture) {
      return false;
    }

    // 市区町村でフィルタリング
    const selectedMunicipalities = this.filter.municipalities[this.filter.prefecture];
    // 市区町村が選択されていない、または配列が空の場合は、都道府県のフィルタリング結果のみを返す
    if (!selectedMunicipalities || selectedMunicipalities.length === 0) {
      return true;
    }

    // 市区町村が選択されている場合、施設情報の市区町村コードが選択されているかを返す
    return selectedMunicipalities.includes(facility.facilityDetailInfo.region.municipalityCode);
  }

  /**
   * ### 施設種別フィルタリング
   */
  private filteringFacilityCategory(facility: RoomScheduleTableModel): boolean {
    // フィルタリングの施設種別が空文字（全て）なら全てを返す
    if (!this.filter.facilityCategory) {
      return true;
    }

    // 施設種別がない場合は非表示で扱う
    if (!facility.facilityDetailInfo || !facility.facilityDetailInfo.facilityCategory) {
      return false;
    }

    // 施設種別でフィルタリング
    if (facility.facilityDetailInfo.facilityCategory !== this.filter.facilityCategory) {
      return false;
    }

    return true;
  }

  /**
   * ### 介護度フィルタリング
   */
  private filteringCareLevels(facility: RoomScheduleTableModel): boolean {
    // フィルタリングの介護度が全てfalseなら全てを返す
    let exist: boolean = false;
    for (const careLevel of Object.values(this.filter.careLevels)) {
      if (careLevel) {
        exist = true;
        break;
      }
    }
    if (!exist) {
      return true;
    }

    // 介護度がない場合は非表示で扱う
    if (!facility.facilityDetailAccept || !facility.facilityDetailAccept.careLevels) {
      return false;
    }

    // 介護度でフィルタリング
    // フィルタの介護度がtrue かつ 施設の受け入れ可能な介護度の同名プロパティがtrueであればtrueを返す
    for (const key of Object.keys(this.filter.careLevels)) {
      const careLevelKey = key as keyof CareLevels;
      if (this.filter.careLevels[careLevelKey] && facility.facilityDetailAccept.careLevels[careLevelKey]) {
        return true;
      }
    }

    return false;
  }

  /**
   * ### 生活保護を受け入れるか否かフィルタリング
   */
  private filterOnlyAcceptsWelfare(facility: RoomScheduleTableModel): boolean {
    // 受け入れ可 施設のみでなければ全てを返す
    if (!this.filter.acceptsWelfare) {
      return true;
    }

    // 施設の追加情報が無ければ非表示対象
    if (!facility.facilityDetailAccept) {
      return false;
    }
    // 施設の追加情報があれば 生活保護を受け入れるか否か の値を返す
    else {
      if (facility.facilityDetailAccept.acceptsWelfare) {
        return true;
      } else {
        return false;
      }
    }
  }

  /**
   * ### 医療・看護体制フィルタリング
   */
  private filterMedicalNursings(facility: RoomScheduleTableModel): boolean {
    // フィルタリングの医療・看護体制が全てfalseなら全てを返す
    let exist: boolean = false;
    for (const medicalNursing of Object.values(this.filter.medicalNursings)) {
      if (medicalNursing) {
        exist = true;
        break;
      }
    }
    if (!exist) {
      return true;
    }

    // 医療・看護体制がない場合は非表示で扱う
    if (!facility.facilityDetailAccept || !facility.facilityDetailAccept.medicalNursings) {
      return false;
    }

    // 医療・看護体制でフィルタリング
    // フィルタの医療・看護体制がtrueの場合に、施設の受け入れ可能な医療・看護体制の同名プロパティがすべてtrueであればtrueを返す(AND条件)
    for (const key of Object.keys(this.filter.medicalNursings)) {
      const medicalNursingKey = key as keyof MedicalNursings;
      if (this.filter.medicalNursings[medicalNursingKey] && !facility.facilityDetailAccept.medicalNursings[medicalNursingKey]) {
        return false;
      }
    }

    return true;
  }

  private filterOnlyBlankRoom(facility: RoomScheduleTableModel): boolean {
    // 使用しないであれば全てを返す
    if (!this.filter.onlyBlankRoom.use) {
      return true;
    }

    // 公開情報の値で分岐し、それぞれ選択された日付に空床があるかを返す
    const day: number = this.$dateUtil.convertDateToNumber(new Date(this.filter.onlyBlankRoom.date), "dd");

    // 空床スケジュールの場合
    if (facility.publicType == PublicType.SCHEDULE) {
      // フィルタリングで指定された日付の空床が1以上ある場合はtrueを返す
      if (facility.useCasesForRoomSchedule) {
        for (const useCase of facility.useCasesForRoomSchedule) {
          for (const room of useCase.rooms) {
            if (1 <= room.days[day - 1]) {
              return true;
            }
          }
        }
      }
    }
    // 公開情報（自分で設定）の場合
    else if (facility.publicType == PublicType.CUSTOM) {
      // 空床情報が〇か電話の部屋がある場合はtrueを返す
      if (facility.useCasesForVacancySetting) {
        for (const useCase of facility.useCasesForVacancySetting) {
          for (const room of useCase.rooms) {
            if (room.receptionStatus == ReceptionStatus.CAN || room.receptionStatus == ReceptionStatus.TEL) {
              return true;
            }
          }
        }
      }
    }

    return false;
  }

  public mounted(): void {
    this.holidaysMap = this.createHolidaysMap(nationalHolidaysStore.holidays);
  }

  public createHolidaysMap(holidays: Array<NationalHolidaysGetModel>): Map<string, NationalHolidaysGetModel> {
    const holidaysMap: Map<string, NationalHolidaysGetModel> = new Map();
    for (const holiday of holidays) {
      holidaysMap.set(holiday.date, holiday);
    }
    return holidaysMap;
  }

  public getDay(day: string): number {
    const date: Date = new Date(day);
    return date.getDate();
  }

  public getWeekClass(day: string): string {
    if (this.holidaysMap.has(this.$dateUtil.convertDateToString(new Date(day), "yyyy-mm-dd"))) {
      return "holiday";
    }

    const week = this.getWeek(day);
    switch (week) {
      case "日": {
        return "sunday";
      }
      case "土": {
        return "saturday";
      }
      default: {
        return "";
      }
    }
  }

  public getWeek(day: string): string {
    const date: Date = new Date(day);
    const week: string | undefined = this.weekNameMap.get(date.getDay());
    return week ? week : "";
  }

  public getHolidayName(day: string): string {
    const holiday: NationalHolidaysGetModel | undefined = this.holidaysMap.get(this.$dateUtil.convertDateToString(new Date(day), "yyyy-mm-dd"));
    if (holiday) {
      return holiday.name;
    } else {
      return "";
    }
  }

  public getFacilityRowSapan(facility: OtherOfficeRoomSchedulesGetModel): number {
    let rowSpan = 0;
    // 家族連絡サービスの空床スケジュールを使用する場合
    if (facility.publicType == PublicType.SCHEDULE) {
      for (const useCase of facility.useCasesForRoomSchedule!) {
        rowSpan += useCase.rooms.length;
      }
    }
    // 自分で設定した空床情報を使用する場合
    else if (facility.publicType == PublicType.CUSTOM) {
      for (const useCase of facility.useCasesForVacancySetting!) {
        rowSpan += useCase.rooms.length;
      }
    }
    return rowSpan;
  }

  public convertFacilityName(facility: RoomScheduleTableModel): string {
    // 施設IDが同じなら自施設と表記する
    if (facility.facilityId == userStore.facility.id) {
      return "自施設";
    } else {
      return facility.name;
    }
  }

  public convertUseTypeName(useCase: UseType): string {
    let useCaseName: string = "";
    switch (useCase) {
      case UseType.NORMAL_STAY: {
        useCaseName = "長期入所";
        break;
      }
      case UseType.SHORT_STAY: {
        useCaseName = "ショートステイ";
        break;
      }
      default: {
        return "";
      }
    }
    return useCaseName;
  }

  public convertUseCaseName(useCase: UseCase): string {
    let useCaseName: string = "";
    switch (useCase) {
      case UseCase.LONG_STAY: {
        useCaseName = "長期入所";
        break;
      }
      case UseCase.SHORT_STAY: {
        useCaseName = "ショートステイ";
        break;
      }
      case UseCase.LONG_AND_SHORT_STAY: {
        useCaseName = "長期・短期併用";
        break;
      }
      default: {
        return "";
      }
    }
    return useCaseName;
  }

  public convertRoomTypeNameForFamilyService(roomType: RoomScheduleRoomType): string {
    let roomTypeName: string = "";
    switch (roomType) {
      case RoomScheduleRoomType.SINGLE: {
        roomTypeName = "個室";
        break;
      }
      case RoomScheduleRoomType.DOUBLE: {
        roomTypeName = "2人部屋（夫婦部屋）";
        break;
      }
      case RoomScheduleRoomType.MULTI_MEN: {
        roomTypeName = "多床室（男性）";
        break;
      }
      case RoomScheduleRoomType.MULTI_WOMEN: {
        roomTypeName = "多床室（女性）";
        break;
      }
      default: {
        roomTypeName = "";
      }
    }
    return `${roomTypeName}`;
  }

  public convertRoomTypeName(roomType: RoomType): string {
    let roomTypeName: string = "";
    switch (roomType) {
      case RoomType.SINGLE: {
        roomTypeName = "個室";
        break;
      }
      case RoomType.MULTI_BED: {
        roomTypeName = "多床室";
        break;
      }
      case RoomType.MULTI_BED_MEN: {
        roomTypeName = "多床室（男性）";
        break;
      }
      case RoomType.MULTI_BED_WOMEN: {
        roomTypeName = "多床室（女性）";
        break;
      }
      case RoomType.MEN: {
        roomTypeName = "男性";
        break;
      }
      case RoomType.WOMEN: {
        roomTypeName = "女性";
        break;
      }
      default: {
        return "";
      }
    }
    return `${roomTypeName}`;
  }

  public convertReceptionStatusAndCount(room: RoomForVacancySetting): string {
    if (room.receptionStatus == ReceptionStatus.CAN) {
      if (room.receptionCount == ReceptionCount.NOT_PUBLIC) {
        return `${this.convertReceptionStatus(room.receptionStatus as ReceptionStatus)}`;
      } else {
        return `${this.convertReceptionStatus(room.receptionStatus as ReceptionStatus)}（${this.convertReceptionCount(room.receptionCount as ReceptionCount)}）`;
      }
    } else {
      return `${this.convertReceptionStatus(room.receptionStatus as ReceptionStatus)}`;
    }
  }

  public convertReceptionStatus(receptionStatus: ReceptionStatus): string {
    switch (receptionStatus) {
      case ReceptionStatus.CANT: {
        return "×";
      }
      case ReceptionStatus.CAN: {
        return "〇";
      }
      case ReceptionStatus.TEL: {
        return "電話";
      }
      default: {
        return "";
      }
    }
  }

  public convertReceptionCount(receptionCount: ReceptionCount): string {
    switch (receptionCount) {
      case ReceptionCount.NOT_PUBLIC: {
        return "非公開";
      }
      case ReceptionCount.BED_1: {
        return "1床";
      }
      case ReceptionCount.BED_2: {
        return "2床";
      }
      case ReceptionCount.BED_3: {
        return "3床";
      }
      case ReceptionCount.BED_4: {
        return "4床";
      }
      case ReceptionCount.BED_5: {
        return "5床";
      }
      case ReceptionCount.BED_6: {
        return "6床";
      }
      case ReceptionCount.BED_7: {
        return "7床";
      }
      case ReceptionCount.BED_8: {
        return "8床";
      }
      case ReceptionCount.BED_9: {
        return "9床";
      }
      case ReceptionCount.BED_OVER_10: {
        return "10床以上";
      }
      default: {
        return "";
      }
    }
  }

  public changeFacilityCheck(): void {
    this.$emit("changeFacilityCheck");
    return;
  }
}

export interface RoomScheduleTableModel extends OtherOfficeRoomSchedulesGetModel {
  /** 選択状態 */
  checked: boolean;
}

export interface FilterSetting {
  /** 地域（都道府県） */
  prefecture: string;
  /** 地域（市区町村） */
  municipalities: { [key: string]: string[] };
  /** 施設種別 */
  facilityCategory: FacilityCategory | "";
  /** 介護度（複数選択可能） */
  careLevels: CareLevels;
  /** 空床有り施設のみ */
  onlyBlankRoom: {
    /** 使用可否 */
    use: boolean;
    /** 日時 */
    date: string;
  };
  /** 生活保護を受け入れるか否か */
  acceptsWelfare: boolean;
  /** 医療・看護体制 */
  medicalNursings: MedicalNursings;
}
</script>
